import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/app/admin/_common/ui/sheet";
import CuoButton from "@/app/admin/_common/CuoButton";
import { EmailForm } from "./EmailForm";
import { useEmailFormFlow } from "./useEmailFormFlow";
import { Mail, X } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

export const EmailDialog = ({
  open,
  campaignId,
  onClose,
  onCreated,
}: {
  open: boolean;
  campaignId?: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) => {
  const flow = useEmailFormFlow({
    open,
    campaignId,
    onCreated: (id) => {
      onCreated(id);
      onClose();
    },
  });
  const { isSaving, handleSave, resetForm } = flow;
  const isMobile = useIsMobile();

  function handleClose() {
    resetForm();
    onClose();
  }

  const description = "Name the email, write its subject line, then pick a template to start from.";

  const form = (
    <EmailForm
      control={flow.control}
      errors={flow.errors}
      templates={flow.templates}
      templatesLoading={flow.templatesLoading}
      templateSearch={flow.templateSearch}
      setTemplateSearch={flow.setTemplateSearch}
      selectedTemplateId={flow.selectedTemplateId}
      selectTemplate={flow.selectTemplate}
    />
  );

  const footerButtons = (
    <>
      <CuoButton variant="outline" label="Cancel" onClick={handleClose} />
      <CuoButton
        variant="default"
        label="Create"
        disableOnAsync={isSaving}
        onClick={handleSave}
      />
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && handleClose()}>
        <SheetContent
          side="right"
          hideClose
          className="flex h-full w-screen max-w-none flex-col gap-0 border-l-0 bg-white p-0 sm:max-w-none"
        >
          <SheetTitle className="sr-only">New Email</SheetTitle>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="inline-flex items-center gap-2 text-[13px] font-normal text-foreground">
              <Mail className="size-4 flex-none text-muted-foreground" />
              New Email
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            <p className="text-[12.5px] text-muted-foreground">{description}</p>
            {form}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border p-3">
            {footerButtons}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto h-fit max-h-[85vh] w-full max-w-[860px] translate-x-0 translate-y-0 bg-white">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <Mail className="size-4 flex-none text-muted-foreground" />
              New Email
            </span>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-2">{form}</div>

        <DialogFooter>{footerButtons}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
